import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/common/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../components/ui/common/dialog";
import {
  mappingDialogBodyClass,
  mappingDialogContentClass,
  mappingDialogFooterClass,
  mappingDialogHeaderClass,
  mappingDialogLoadingOverlayClass
} from "../mappingModalStyles";
import DeleteConfirmPhraseInput from "./components/DeleteConfirmPhraseInput";
import DeleteScopeRadioGroup from "./components/DeleteScopeRadioGroup";
import { DELETE_MAPPING_COPY } from "./constants/deleteMapping.constants";
import { useDeleteMappingDialog } from "./hooks/useDeleteMappingDialog";
import { useDeleteMappingSubmit } from "./hooks/useDeleteMappingSubmit";
import { DeleteMappingDialogProps } from "./types/deleteMapping.types";

/**
 * "Remove mapping" -- removes either the active mapping version or the mapping's whole history.
 *
 * Layout and wiring only: scope/phrase state lives in useDeleteMappingDialog and the endpoint
 * choice in useDeleteMappingSubmit. Radix supplies Escape, backdrop dismiss, the focus trap and
 * focus restore, so the only close behaviour written here is the in-flight guard.
 */
const DeleteMappingDialog = ({
  open,
  onOpenChange,
  drawingId,
  mappingSourceType,
  currentVersionId,
  currentVersionNumber,
  versionCount,
  hideAllVersions,
  onDeleted
}: DeleteMappingDialogProps) => {
  const {
    scope,
    selectScope,
    confirmText,
    setConfirmText,
    requiredPhrase,
    isConfirmed,
    destructiveLabel,
    mode
  } = useDeleteMappingDialog({ open, versionCount, currentVersionId });

  const { submit, isDeleting } = useDeleteMappingSubmit({
    drawingId,
    mappingSourceType,
    currentVersionId,
    mode,
    onDeleted,
    onClose: () => onOpenChange(false)
  });

  return (
    <Dialog
      open={open}
      // A delete in flight must not be dismissable -- the mapping would be gone with the dialog
      // still promising the user a choice.
      onOpenChange={next => {
        if (!isDeleting) {
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className={mappingDialogContentClass}>
        <DialogHeader className={mappingDialogHeaderClass}>
          <div className="tw-flex tw-items-center tw-gap-2">
            <AlertTriangle className="tw-h-5 tw-w-5 tw-text-destructive" />
            <DialogTitle>{DELETE_MAPPING_COPY.title}</DialogTitle>
          </div>
          <DialogDescription>{DELETE_MAPPING_COPY.description}</DialogDescription>
        </DialogHeader>

        <div className={mappingDialogBodyClass}>
          {isDeleting ? (
            <div className={mappingDialogLoadingOverlayClass}>
              <Loader2 className="tw-h-6 tw-w-6 tw-animate-spin tw-text-primary" />
            </div>
          ) : null}

          <DeleteScopeRadioGroup
            value={scope}
            onChange={selectScope}
            currentVersionNumber={currentVersionNumber}
            mode={mode}
            disabled={isDeleting}
            hideAllVersions={hideAllVersions}
          />

          <DeleteConfirmPhraseInput
            value={confirmText}
            onChange={setConfirmText}
            requiredPhrase={requiredPhrase}
            disabled={isDeleting}
          />
        </div>

        <DialogFooter className={mappingDialogFooterClass}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isDeleting}
            onClick={() => onOpenChange(false)}
          >
            {DELETE_MAPPING_COPY.cancel}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={!isConfirmed || isDeleting}
            onClick={() => submit(scope)}
          >
            {destructiveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteMappingDialog;
